<template>
  <div :id="id" ref="input" class="condition">
    <div
      v-if="canEdit && isEditing && type == 'elseIfCondition'"
      class="remove"
      @mousedown="onRemove"
    >
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        x="0px"
        y="0px"
        viewBox="0 0 213 213"
        xml:space="preserve"
      >
        <path
          d="M131.8,106.5l75.9-75.9c7-7,7-18.3,0-25.3c-7-7-18.3-7-25.3,0l-75.9,75.9L30.6,5.2c-7-7-18.3-7-25.3,0c-7,7-7,18.3,0,25.3l75.9,75.9L5.2,182.4c-7,7-7,18.3,0,25.3c7,7,18.3,7,25.3,0l75.9-75.9l75.9,75.9c7,7,18.3,7,25.3,0c7-7,7-18.3,0-25.3L131.8,106.5z"
        />
      </svg>
    </div>
    <div class="wrapper" @dblclick="onWrapperDblClick">
      <div v-touch="onTouch" class="clause" @dblclick.stop="onClauseDblClick">
        {{ clause }}
      </div>
      <rich-text-editor
        v-if="type == 'ifCondition' || type == 'elseIfCondition'"
        ref="editor"
        :editable="canEdit"
        class="script"
        :activate-on-double-click="true"
        :extension-options="extensionOptions"
        :class="{ disabled: !canEdit }"
        :content="content"
        @focus="onFocusScript"
        @blur="onBlurScript"
        @keyup.esc="onBlurScript"
        @update="onUpdateContent"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import _ from 'lodash';
import { JsPlumbInstance } from '@jsplumb/core';
import jspSingleton from '@/lib/jspSingleton.js';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, JSPLUMB } from '@/constants.js';
import { stripCode, removeEnclosingParagraph } from '../../../utils.js';
import RichTextEditor from '../../all/RichText/RichTextEditor.vue';
import resizeObserverWrapper from '../../resize-observer-wrapper.js';

export default {
  components: {
    RichTextEditor,
  },

  props: {
    // The condition id
    id: {
      type: String,
      default: null,
    },
    // The condition type ('ifCondition', 'elseIfCondition', 'elseCondition')
    type: {
      type: String,
      default: null,
    },
    // The branch that this condition belongs
    branchId: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      isEditing: false,
      extensionOptions: {
        hardbreak: false,
        history: true,
        placeholder: {
          text: 'Enter condition here...',
        },
        dropEventHandler: true,
        customMention: true,
        codeBlockHighlight: { toolbarButton: false },
        code: true,
      },
      updatedCode: null, // The current code (probably not yet saved)
      isRemoved: false, // when the condition is on removal process do not save scripts
    };
  },

  computed: {
    ...mapGetters('project/data/conditions', ['getNode']),
    ...mapGetters('project/data/branches', { getBranch: 'getNode' }),
    ...mapGetters('project/data/variables', ['getAllNodes']),

    /**
     * Return the current condition
     * @returns {Object}
     */
    condition() {
      return this.getNode(this.id);
    },

    /**
     * Return the condition text ('if', 'elseif', 'else'). Remove 'Condition' from type and convert elseIf to elseif.
     */
    clause() {
      return this.type.substring(0, this.type.length - 9).toLowerCase();
    },

    /**
     * Return the current content.
     * The updated code is required to keep the <pre><code> tags when the script is not yet saved to show highlight.
     */
    content() {
      if (this.condition?.script) {
        return `<pre><code>${this.condition.script}</code></pre>`;
      }
      return null;
    },
  },

  watch: {
    content(newVal, oldVal) {
      console.log('BoardBranchCondition content is now:', newVal);
    },
  },

  /**
   * Vue mounted lifecycle hook
   */
  mounted() {
    // Create an endpoint for the condition
    jspSingleton.instance.addEndpoint(this.$el, JSPLUMB.sourceEnpointCondition);

    // Whenever a resize happens revalidate this element
    resizeObserverWrapper.add(this.$el, () => {
      this.$nextTick(() => {
        jspSingleton.instance.revalidate(this.$el); // we need to repaint the element's endpoints-connections
      });
    });
  },

  /**
   * Vue beforeUnmount lifecycle hook
   */
  beforeUnmount() {
    // This cleanup will also be handled by the BoardBranch
    resizeObserverWrapper.remove(this.$el);

    jspSingleton.instance.removeAllEndpoints(this.$el);
  },

  methods: {
    ...mapActions('project/data/conditions', ['customUpdate']),
    ...mapActions('project/data/branches', ['deleteElseIfCondition']),

    /**
     * Event handler: custom update event from rich text editor.
     */
    onUpdateContent(content) {
      this.updatedCode = removeEnclosingParagraph(content);
    },

    /**
     * Event handler: the user clicks on the remove button for the condition
     */
    onRemove() {
      this.isRemoved = true;
      this.deleteElseIfCondition({
        id: this.branchId,
        cid: this.id,
        apply: true,
      });
    },

    /**
     * Event handler: the RichTextEditor gains the focus (custom event)
     */
    onFocusScript() {
      this.isEditing = true;
      // Disable branch dragging
      this.$emit('disableBranch');
    },

    /**
     * Event handler: the RichTextEditor loses the focus (custom event)
     * @param {String} content  The rich text editor content is sent with the event
     */
    onBlurScript(content) {
      this.isEditing = false;
      // Remove any html entities from the script
      if (!this.isRemoved) {
        const script = _.unescape(stripCode(content));
        this.customUpdate({
          id: this.id,
          props: { script },
          apply: true,
        });
      }

      // Re-enable branch dragging
      this.$emit('enableBranch');
    },

    /**
     * Event handler: User double clicks on the clause name
     */
    onClauseDblClick() {
      if (this.$refs.editor) {
        // Start editing and put caret at start (nearest to clause)
        this.$refs.editor.startEditing('start');
      }
      console.log('DBLCLICK CLAUSE');
    },

    /**
     * Event listener - touch event on branch. We will handle all touch events here.
     */
    onTouch(event) {
      if (event.type === 'doubletap') {
        console.log('*******CONDITION DOUBLE TAP', event);
        this.onClauseDblClick(event);
      }
      event.stopPropagation();
    },

    /**
     * Event handler: User double clicks on the wrapper name
     */
    onWrapperDblClick() {
      if (this.$refs.editor) {
        // Start editing and put caret at start (nearest to clause)
        this.$refs.editor.startEditing('end');
      }
      console.log('DBLCLICK WRAPPER');
    },
  },
};
</script>

<style scoped lang="scss">
.condition {
  position: relative;
  left: auto !important;
  top: auto !important;
  background: #0d0e0e;
  .remove {
    // opacity: 0;
    position: absolute;
    top: -10px;
    right: 20px;
    color: #fff;
    font-weight: 700;
    cursor: pointer;
    background: #4f5e61;
    border-radius: 999px;
    width: 20px;
    height: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;
    &:hover {
      background: #db841e;
      svg path {
        fill: #fff;
      }
    }
    svg {
      width: 8px;
      height: 8px;
    }
  }
  .wrapper {
    padding: 16px 44px 15px 20px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    font-size: 14px;
    font-family: monospace;
    overflow: hidden;

    :deep(.ProseMirror pre) {
      background: none;
      padding: 0;
      z-index: auto;
    }

    .clause {
      color: #fff;
      font-weight: 600;
      margin: 0 12px 0 0;
    }
    .script {
      flex: 1;
      :deep(.validation) {
        top: 17px;
        right: 16px;
      }
    }
    .container {
      &[contenteditable='true']:empty:before {
        font-family: 'Open Sans';
        content: attr(placeholder);
        pointer-events: none;
        display: block; /* For Firefox */
        color: #fff;
        opacity: 0.3;
      }
    }
  }
}
</style>

<style lang="scss"></style>
